import { useEffect, useRef } from 'react';
import { RotateCcw, X } from 'lucide-react';
import type {
  EditorAppearance,
  EditorBackground,
  EditorRuleLines,
  EditorParagraphGap,
} from '../hooks/useEditorAppearance';

interface EditorAppearancePanelProps {
  appearance: EditorAppearance;
  onUpdate: <K extends keyof EditorAppearance>(key: K, value: EditorAppearance[K]) => void;
  onReset: () => void;
  onClose: () => void;
}

const BACKGROUNDS: Array<{ id: EditorBackground; label: string }> = [
  { id: 'default', label: '跟随主题' },
  { id: 'white', label: '纸张白' },
  { id: 'beige', label: '米黄' },
  { id: 'green', label: '护眼绿' },
  { id: 'slate', label: '浅灰' },
];

const RULE_LINES: Array<{ id: EditorRuleLines; label: string }> = [
  { id: 'none', label: '无' },
  { id: 'solid', label: '实线' },
  { id: 'dashed', label: '虚线' },
];

const PARAGRAPH_GAPS: Array<{ id: EditorParagraphGap; label: string }> = [
  { id: 'compact', label: '紧凑' },
  { id: 'standard', label: '标准' },
  { id: 'wide', label: '宽松' },
];

/**
 * 编辑区外观设置面板：
 * 背景色 / 稿纸行线（实线、虚线）/ 段落间距 / 首行缩进，
 * 全部仅作用于编辑区显示，不修改章节内容。
 */
export function EditorAppearancePanel({ appearance, onUpdate, onReset, onClose }: EditorAppearancePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // 点击面板外部或按 Esc 关闭
  useEffect(() => {
    const handlePointerDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="editor-appearance-panel" ref={panelRef} onMouseDown={e => e.stopPropagation()}>
      <div className="ea-header">
        <span className="ea-title">编辑区外观</span>
        <div className="ea-header-actions">
          <button
            type="button"
            className="ea-icon-btn"
            onClick={onReset}
            title="恢复默认设置"
          >
            <RotateCcw size={13} />
          </button>
          <button type="button" className="ea-icon-btn" onClick={onClose} title="关闭">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* 编辑区背景 */}
      <div className="ea-section">
        <div className="ea-section-label">编辑区背景</div>
        <div className="ea-swatches">
          {BACKGROUNDS.map(item => (
            <button
              key={item.id}
              type="button"
              className={`ea-swatch ea-swatch-${item.id}${appearance.background === item.id ? ' is-active' : ''}`}
              title={item.label}
              onClick={() => onUpdate('background', item.id)}
            />
          ))}
        </div>
        <div className="ea-swatch-names">
          {BACKGROUNDS.map(item => (
            <span key={item.id} className={appearance.background === item.id ? 'is-active' : ''}>
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* 稿纸行线 */}
      <div className="ea-section">
        <div className="ea-section-label">稿纸行线</div>
        <div className="ea-segment">
          {RULE_LINES.map(item => (
            <button
              key={item.id}
              type="button"
              className={`ea-segment-option${appearance.ruleLines === item.id ? ' is-active' : ''}`}
              onClick={() => onUpdate('ruleLines', item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="ea-section-hint">在每行文字下方显示横向参考线，方便对齐书写</div>
      </div>

      {/* 段落间距 */}
      <div className="ea-section">
        <div className="ea-section-label">段落间距</div>
        <div className="ea-segment">
          {PARAGRAPH_GAPS.map(item => (
            <button
              key={item.id}
              type="button"
              className={`ea-segment-option${appearance.paragraphGap === item.id ? ' is-active' : ''}`}
              // 稿纸行线模式下段落间距由行线节奏决定（必须是整行高，实际归零），
              // 再点也不会有变化 —— 直接置灰，避免"调了没反应"。
              disabled={appearance.ruleLines !== 'none'}
              onClick={() => onUpdate('paragraphGap', item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="ea-section-hint">
          {appearance.ruleLines === 'none'
            ? '拉大段落间隔，可从视觉上区分段落边界'
            : '稿纸行线模式下段落间距并入行线节奏（整行高），段落由首行缩进区分'}
        </div>
      </div>

      {/* 首行缩进 */}
      <div className="ea-section ea-section-row">
        <div>
          <div className="ea-section-label">首行缩进</div>
          <div className="ea-section-hint">每段首行缩进 2 字符（中文小说常见排版）</div>
        </div>
        <button
          type="button"
          className={`ea-switch${appearance.paragraphIndent ? ' is-on' : ''}`}
          role="switch"
          aria-checked={appearance.paragraphIndent}
          title={appearance.paragraphIndent ? '关闭首行缩进' : '开启首行缩进'}
          onClick={() => onUpdate('paragraphIndent', !appearance.paragraphIndent)}
        >
          <span className="ea-switch-thumb" />
        </button>
      </div>
    </div>
  );
}
